// Who to contact at this employer.
//
// Every field is optional, so continuing with nothing filled in is the skip —
// there is no separate "skip" control to read as skipping the whole entry. When
// we already hold contacts the first is selected, because a pre-selected known
// contact is the answer most of the time and the driver can move it.

import { formatPhone } from 'src/lib/phone.utils'

import type { ContactChoice } from '../lib/entryFlow'
import type { Errors } from '../lib/validation'

import { ResultRow } from './ResultRow'
import { SectionHeader } from './SectionHeader'

export interface ContactStepProps {
  known: ContactChoice[]
  chosen: ContactChoice | null
  onChoose: (contact: ContactChoice) => void
  /** Free-text entry, used when nothing is held or the driver wants another. */
  manual: { name?: string | null; phone?: string | null; email?: string | null }
  onManual: (patch: {
    name?: string | null
    phone?: string | null
    email?: string | null
  }) => void
  onBlur?: () => void
  errors?: Errors
  /** True once the driver has asked to type one instead of picking. */
  entering: boolean
  onEnterDifferent: () => void
  onFixChosen?: () => void
  /** Back to the list. Only offered when there is a list to go back to. */
  onBackToResults?: () => void
  /** Still finding out what we hold for this employer. */
  loading?: boolean
}

/**
 * Punctuate what the driver typed, without turning a country code into an area
 * code.
 *
 * formatPhone strips a leading "+1" but not a bare "1", and the formatter behind
 * it keeps only the first ten digits — so typing or pasting 1 617 834 4316 became
 * (161) 783 - 4431: a different number that still passes validation and would
 * have gone out on a verification call.
 */
const maskPhone = (value: string): string => {
  const digits = value.replace(/\D/g, '')
  const national =
    digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits
  return formatPhone(national)
}

const field =
  'w-full border-b border-doubleNickel-gray-300 bg-transparent py-1 text-[12.5px] text-doubleNickel-gray-900 outline-none placeholder:text-doubleNickel-gray-400'

export const ContactStep = ({
  known,
  chosen,
  onChoose,
  manual,
  onManual,
  onBlur,
  errors = {},
  entering,
  onEnterDifferent,
  onFixChosen,
  onBackToResults,
  loading = false,
}: ContactStepProps) => {
  const chosenProblems = [errors.phone, errors.email].filter(
    (message): message is string => Boolean(message)
  )
  const isChosen = (contact: ContactChoice) =>
    chosen != null &&
    (chosen.contactId
      ? chosen.contactId === contact.contactId
      : chosen.name === contact.name)

  return (
    <div className="rounded-[13px] border border-doubleNickel-gray-200 bg-white px-3 py-2.5">
      {/*
      No count when we hold nothing: the fields below are already open, so
      announcing "No results" only tells a driver something failed before they
      have done anything.
    */}
      <SectionHeader
        title="Contact"
        count={
          known.length > 0
            ? `${known.length} ${known.length === 1 ? 'result' : 'results'}`
            : undefined
        }
        complete={known.length > 0}
      />

      {loading && (
        <div
          role="status"
          className="mt-1.5 text-[12px] text-doubleNickel-gray-500"
        >
          Checking this employer…
        </div>
      )}

      {!loading && known.length > 0 && !entering && (
        <div className="mt-1.5 flex flex-col gap-1.5">
          {known.map((contact, index) => {
            const selected = isChosen(contact)
            const invalid = selected && chosenProblems.length > 0
            return (
              <div key={contact.contactId ?? `${contact.name}-${index}`}>
                <ResultRow
                  name={contact.name ?? 'Contact'}
                  details={[contact.phone, contact.email]}
                  selected={selected}
                  invalid={invalid}
                  describedBy={invalid ? 'chosen-contact-problem' : undefined}
                  onSelect={() =>
                    onChoose({ ...contact, source: 'known_chosen' })
                  }
                />
                {invalid && (
                  <div className="mt-1 flex items-start justify-between gap-2 px-1">
                    <div
                      id="chosen-contact-problem"
                      role="alert"
                      className="text-[10.5px] text-doubleNickel-error-700"
                    >
                      {chosenProblems.join(' ')}
                    </div>
                    {onFixChosen && (
                      <button
                        type="button"
                        onClick={onFixChosen}
                        className="flex-none text-[11px] font-semibold text-doubleNickel-brand-600"
                      >
                        Fix this contact
                      </button>
                    )}
                  </div>
                )}
              </div>
            )
          })}
          <ResultRow
            name="Enter a different contact"
            action
            onSelect={onEnterDifferent}
          />
        </div>
      )}

      {!loading && (known.length === 0 || entering) && (
        <div className="mt-1.5 flex flex-col gap-2">
          {/*
          Typing is not a one-way door. Without this the only way back to the
          contacts we hold was to leave the sheet and start the entry again.
        */}
          {entering && known.length > 0 && onBackToResults && (
            <button
              type="button"
              onClick={onBackToResults}
              className="self-start text-[12px] font-semibold text-doubleNickel-brand-600"
            >
              ‹ Choose from {known.length}{' '}
              {known.length === 1 ? 'contact' : 'contacts'} we have
            </button>
          )}
          <div>
            <div className="mb-0.5 text-[10px] font-medium text-doubleNickel-gray-600">
              Name
            </div>
            <input
              type="text"
              aria-label="Contact name"
              value={manual.name ?? ''}
              placeholder="Contact name"
              onChange={(event) => onManual({ name: event.target.value })}
              onBlur={onBlur}
              className={field}
            />
          </div>
          <div>
            <div className="mb-0.5 text-[10px] font-medium text-doubleNickel-gray-600">
              Phone
            </div>
            {/*
            Punctuated as it is typed, with the same helper the rest of the
            application uses. Left plain, the placeholder was the only hint
            about whether brackets were expected.

            maskPhone handles both forms of country code — a stored "+1" and a
            typed bare "1" — before formatting.
          */}
            <input
              type="tel"
              inputMode="tel"
              aria-label="Contact phone"
              value={maskPhone(manual.phone ?? '')}
              placeholder="(555) 555 - 0100"
              onChange={(event) =>
                onManual({ phone: maskPhone(event.target.value) })
              }
              onBlur={onBlur}
              className={field}
            />
            {errors.phone && (
              <div className="mt-0.5 text-[10px] text-doubleNickel-error-700">
                {errors.phone}
              </div>
            )}
          </div>
          <div>
            <div className="mb-0.5 text-[10px] font-medium text-doubleNickel-gray-600">
              Email
            </div>
            <input
              type="email"
              aria-label="Contact email"
              value={manual.email ?? ''}
              placeholder="name@company.com"
              onChange={(event) => onManual({ email: event.target.value })}
              onBlur={onBlur}
              className={field}
            />
            {errors.email && (
              <div className="mt-0.5 text-[10px] text-doubleNickel-error-700">
                {errors.email}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
